import { useEffect, useState, useRef } from 'react';
import KitchenAccordion from '../../helpers/kitchen-accordion';
import styles from './styles/kitchens.module.css'
import imageData from '../../data/Services/KitchensData.json';
import FAB from '../../helpers/fab'

const useSwipe = (onSwipeLeft, onSwipeRight) => {
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const onTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        onSwipeLeft();
      } else {
        onSwipeRight();
      }
    }
  };

  return { onTouchStart, onTouchEnd };
};

const Kitchens = () => {
  const [kitchens, setKitchens] = useState([]);
  const [modalState, setModalState] = useState({ kitchenIndex: null, imageIndex: null });

  const groupRefs = useRef([]); // Ref to store references to each kitchen group

  useEffect(() => {
    setKitchens(imageData);
  }, []);

  useEffect(() => {
    if (groupRefs.current.length === 0) return;

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.visible);
          observer.unobserve(entry.target); // optional: only animate once
        }
      });
    }, { threshold: 0 });

    requestAnimationFrame(() => {
      groupRefs.current.forEach((group) => {
        if (group) observer.observe(group);
      });
    });

    return () => observer.disconnect();
  }, [kitchens]); // run this when kitchens have been set
    
  const openModal = (kitchenIndex, imageIndex) => {
    setModalState({ kitchenIndex, imageIndex });
  };

  const closeModal = () => {
    setModalState({ kitchenIndex: null, imageIndex: null });
  };

  const prevImage = () => {
    setModalState((prev) => {
      if (prev.kitchenIndex === null) return prev;
      const kitchenImages = kitchens[prev.kitchenIndex].images;
      const newIndex = prev.imageIndex === 0 ? kitchenImages.length - 1 : prev.imageIndex - 1;
      return { ...prev, imageIndex: newIndex };
    });
  };

  const nextImage = () => {
    setModalState((prev) => {
      if (prev.kitchenIndex === null) return prev;
      const kitchenImages = kitchens[prev.kitchenIndex].images;
      const newIndex = (prev.imageIndex + 1) % kitchenImages.length;
      return { ...prev, imageIndex: newIndex };
    });
  };

  // Add state to track the mouse position
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [zoomScale, setZoomScale] = useState(1);  // Default scale is 1
  const imageWrapperRef = useRef(null);

  // Mouse move handler to track mouse position inside the image wrapper
  const handleMouseMove = (e) => {
    if (imageWrapperRef.current) {
      const { left, top, width, height } = imageWrapperRef.current.getBoundingClientRect();
      const x = ((e.clientX - left) / width) * 100;
      const y = ((e.clientY - top) / height) * 100;
      setMousePos({ x, y });
      setZoomScale(1.5);  // Apply zoom scale when mouse moves
    }
  };

  // Reset zoom when mouse leaves the image
  const handleMouseLeave = () => {
    setZoomScale(1);  // Reset to normal scale
  };

  const isModalOpen = modalState.kitchenIndex !== null;

  // Attach swipe handlers
  const swipeHandlers = useSwipe(nextImage, prevImage);

  return (
    <div className={styles.servicePage}>
      <section className={styles.kitchenHero}>
        <img src="https://ik.imagekit.io/1stopkitchens/images/services/kitchens/background.png" style={{display: 'none'}} alt="preload" loading="eager" /> {/* Added for preload */}
        <div className={styles.kitchenHeroOverlay} />
        <h1 className={styles.kitchenHeroTitle}>Kitchens</h1>
      </section>

      <main className={styles.kitchenContent}>
        {/* Info Boxes */}
        <div className={styles.infoBoxes}>
          <div className={styles.infoBox}>
            <h2>Design Consultations</h2>
            <p>Work with experts to build your dream layout. We use state of the art 2020 Technologies to bring your kitchen to life.</p>
          </div>
          <div className={styles.infoBox}>
            <h2>Countertops</h2>
            <p>Quartz, laminate or butcher block – find your perfect surface in your dream custom shape, size and colour!</p>
          </div>
          <div className={styles.infoBox}>
            <h2>Durable Finishes</h2>
            <p>Our cabinets feature a tough polyurethane finish that resists scratches, stains, and water damage, making them perfect for busy kitchens. With easy upkeep and a variety of finishes, your cabinets will stay stylish and protected for years.</p>
          </div>
          <div className={styles.infoBox}>
            <h2>Accessories</h2>
            <p>Explore a variety of smart kitchen accessories, including pull-out trash bins, tray dividers, knife blocks, and more, available with every kitchen installation. Plus, all our cabinets come with soft-close hinges at no extra charge!</p>
          </div>
        </div>

        {/* <iframe src="https://trianglekitchen.com/products/styles" title="Door Styles" width="100%" height="500px"></iframe> */}

        <KitchenAccordion />
        {/* Kitchen Groups */}
        {kitchens.map((kitchen, kitchenIndex) => {
          const images = kitchen.images;
          const featuredImageIndex = images.findIndex(i => i.isFeatured === true);
          const featureImage = featuredImageIndex !== -1 ? images[featuredImageIndex] : images[0];
          const hasMultipleImages = images.length > 1;
          const thumbnails = images.slice(0, 4);
          const remainingCount = images.length - 4;

          return (
            <section 
              key={kitchenIndex} 
              ref={(el) => groupRefs.current[kitchenIndex] = el} 
              className={styles.kitchenGroup} // Initially hidden
            >
              <div className={styles.testimonialGalleryWrapper}>
                <div className={styles.topBorder} />

                {kitchen.testimonial && (
                  <div className={`${styles.testimonialBox} ${styles.fadeIn}`}>
                    <svg className={styles.quoteIcon} viewBox="0 0 24 24" fill="#f4c430" width="24" height="24">
                      <path d="M7.17 6C5.61 6 4.23 7.06 4.03 8.61L3 16h6l1-6H6.59c.19-.6.76-1 1.41-1h.17V6h-.17zm9 0c-1.56 0-2.94 1.06-3.14 2.61L12 16h6l1-6h-3.41c.19-.6.76-1 1.41-1h.17V6h-.17z" />
                    </svg>
                    <i>"{kitchen.testimonial}"</i>
                  </div>
                )}

                <div className={styles.kitchenRow}>
                  <div className={`${styles.featureImage} ${!hasMultipleImages ? styles.singleImage : ''}`}>
                    <img
                      src={featureImage.src}
                      loading="lazy"
                      srcSet={`${featureImage.src}?tr=w-600,h-600,cm-extract,ar-16-9 600w,
                                ${featureImage.src}?tr=w-1200,h-1200,cm-extract,ar-16-9 1200w,
                                ${featureImage.src}?tr=w-1800,h-1800,cm-extract,ar-16-9 1800w`}
                      alt={featureImage.alt}
                      onClick={() => openModal(kitchenIndex, featuredImageIndex)}
                    />
                  </div>

                  {hasMultipleImages && (
                    <div className={styles.thumbnailColumn}>
                      {thumbnails.map((img, index) => {
                        if (img !== featureImage) {
                          const imageIndex = index;
                          const isLastVisible = index === thumbnails.length - 1 && remainingCount > 0;

                          return (
                            <div
                              key={img.id}
                              className={`${styles.thumbItem} ${isLastVisible ? styles.moreThumb : ''}`}
                              onClick={() => openModal(kitchenIndex, imageIndex)}
                            >
                              <img 
                                src={img.src} 
                                alt={img.alt}
                                loading="lazy"
                                srcSet={`${img.src}?tr=w-600,h-600,cm-extract,ar-16-9 600w,
                                        ${img.src}?tr=w-1200,h-1200,cm-extract,ar-16-9 1200w,
                                        ${img.src}?tr=w-1800,h-1800,cm-extract,ar-16-9 1800w`}
                              />
                              {isLastVisible && (
                                <div className={styles.overlayMore}>+{remainingCount}</div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </main>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal} {...swipeHandlers}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close-btn"
              onClick={closeModal}
              aria-label="Close modal"
              title="Close"
            >
              ×
            </button>

            <div
              className="modal-image-wrapper zoom-view"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              ref={imageWrapperRef}
            >
              <img
                src={kitchens[modalState.kitchenIndex].images[modalState.imageIndex].src}
                alt={kitchens[modalState.kitchenIndex].images[modalState.imageIndex].alt}
                className="modal-image"
                style={{
                  transformOrigin: `${mousePos.x}% ${mousePos.y}%`,
                  transform: zoomScale ? `scale(${zoomScale})` : 'scale(1)',  // Apply dynamic zoom
                  transition: 'transform 0.2s ease-out',  // Smooth transition
                }}
              />
            </div>

            <div className="modal-thumbnails">
              {kitchens[modalState.kitchenIndex].images.map((img, index) => (
                <img
                  key={img.id}
                  src={img.src}
                  alt={img.alt}
                  className={`thumbnail-image ${index === modalState.imageIndex ? 'active' : ''}`}
                  onClick={() => setModalState({ ...modalState, imageIndex: index })}
                />
              ))}
            </div>

            {(kitchens[modalState.kitchenIndex]?.images.length > 1) && (
              <div className="modal-nav-buttons">
                <button className="nav-btn prev-btn" onClick={prevImage} aria-label="Previous image" title="Previous image">‹</button>
                <button className="nav-btn next-btn" onClick={nextImage} aria-label="Next image" title="Next image">›</button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {!isModalOpen &&<FAB />}
    </div>
  );
};

export default Kitchens;